// TODO: delete or shorten this file when Operators Page and Licenses Page refactors are done (OC-866)
import { PLATFORM } from '@/constants/platform'
import { ddeApi } from '@/service/ddeApi'
import { useAuthStore } from '@/stores/useAuthStore'
import { FormattedOperator, OperatorWithUsersCount } from '@/types/operator'
import { Platform } from '@/types/platform'
import {
  CreateDDEOperatorData,
  CreateDDEOperatorResponse,
  CreateDGEOperator,
  DGEOperator
} from '@/types/streams/operator'
import { StreamsPaginationType } from '@/types/streams/pagination'
import { formatOperators } from '@/utils/api/operators'
import { Operator } from '@/utils/api/putOperatorsLicenses/putOperatorsLicenses.types'

import { DDE_ENDPOINTS } from './constants/ddeEndpoints'
import { DGE_ENDPOINTS } from './constants/dgeEndpoints'
import { getOperatorPropertyLicenses } from './licensesService'
import { deleteData, fetchData, postData, putData } from './request'

interface TokenResponse {
  ips: []
  token: string
}

export function getOperators<T extends Platform>(operatorId?: string | null, query?: string) {
  const platform = useAuthStore.getState().platform
  let url = null
  let parser = null

  if (platform === PLATFORM.DDE) {
    url = DDE_ENDPOINTS.Operators.get.url
    parser = DDE_ENDPOINTS.Operators.get.parser
  } else if (platform === PLATFORM.DGE) {
    url = DGE_ENDPOINTS.Operators.get.url
  } else {
    throw new Error('Unsupported platform')
  }

  if (operatorId) {
    url += `/${operatorId}`
  }

  if (query) {
    url += query
  }

  return fetchData<
    T extends typeof PLATFORM.DGE
      ? StreamsPaginationType<DGEOperator[]> | DGEOperator
      : FormattedOperator[]
  >({
    endpointData: { url, parser }
  })
}

export async function getOperatorsWithUsersCount(operatorId?: string) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DGE) {
    const operators = await getOperators<typeof PLATFORM.DGE>(
      operatorId,
      '?page.page=1&page.size=2000&page.sort=id,asc&startWith='
    ).then((res) => {
      return operatorId ? (res as DGEOperator) : 'content' in res ? res.content : []
    })

    return formatOperators(PLATFORM.DGE, operators)
  }

  if (platform === PLATFORM.DDE) {
    const [operators, { data: userCount }] = await Promise.all([
      getOperators<typeof PLATFORM.DDE>(operatorId),
      ddeApi.endpoints.users.getUserCountForOperators()
    ])

    const operatorIdCountMap = userCount.reduce((acc: Record<string, number>, curr) => {
      acc[curr.operatorId] = curr.userCount
      return acc
    }, {})

    const operatorsWithUsersCount = operators.map((operator) => ({
      ...operator,
      usersCount: operatorIdCountMap[operator.id.toString()] || 0
    })) as OperatorWithUsersCount[]

    if (operatorId) {
      return [operatorsWithUsersCount[0]]
    }

    return operatorsWithUsersCount
  }

  throw new Error('Unsupported platform')
}

export async function putOperatorsLicensesAndRefreshOperators(
  operators: Operator[],
  competitionIds: string[],
  licenseProperty: string
) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    const updates = operators.flatMap((operator) =>
      operator.licensePeriods.map((period) => ({
        period,
        operator,
        endpointData: DDE_ENDPOINTS.Operators.Operator.Licenses.LicenseProperty.Periods.put(
          operator.id.toString(),
          licenseProperty,
          period?.id?.toString() || ''
        )
      }))
    )

    const requests = updates.map(({ endpointData, period }) => {
      return putData({ endpointData, data: { ...period, subscription: { competitionIds } } })
    })
    const updatedLicenses = await Promise.allSettled(requests)

    const updateStatuses = updatedLicenses.map((res, index) => ({
      status: res.status,
      operatorId: updates[index].operator.id
    }))
    const failedUpdates = updateStatuses.filter(({ status }) => status === 'rejected')
    const updateFailedMessages = failedUpdates.map(
      ({ operatorId }) => `Failed to update license for operator ${operatorId}`
    )

    const operatorPropertyLicenses = await getOperatorPropertyLicenses(licenseProperty)

    return [operatorPropertyLicenses, updateFailedMessages] as const
  }

  throw new Error('Unsupported platform')
}

export async function suspendOperators(licenseProperty: string, operatorIds: string[]) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    const bulkSuspendOperators = await Promise.allSettled(
      operatorIds.map((operatorId) =>
        putData({
          endpointData: DDE_ENDPOINTS.Operators.Operator.Licenses.LicenseProperty.Suspend.put(
            operatorId,
            licenseProperty
          )
        })
      )
    )

    return bulkSuspendOperators.map((operator, index) => ({
      status: operator.status,
      operatorId: operatorIds[index]
    }))
  }

  throw new Error('Unsupported platform')
}

export async function getOperatorToken(operatorId: string) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    return fetchData<TokenResponse>({
      endpointData: DDE_ENDPOINTS.Operators.Operator.Token.get(operatorId)
    }).then(({ token }) => token)
  }

  throw new Error('Unsupported platform')
}

type CreateDGEOperatorPostModel = Omit<CreateDGEOperator['data'], 'id' | 'createdAt'>

export async function createOperator(data: CreateDDEOperatorData | CreateDGEOperatorPostModel) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    return postData<CreateDDEOperatorData, { message: string }>({
      endpointData: DDE_ENDPOINTS.Operators.post,
      data: data as CreateDDEOperatorData
    })
  } else if (platform === PLATFORM.DGE) {
    return putData<CreateDGEOperator['data'], CreateDDEOperatorResponse>({
      endpointData: DGE_ENDPOINTS.Operators.put,
      data: data as CreateDGEOperator['data']
    })
  }

  throw new Error('Unsupported platform')
}

export function deleteOperator(operatorId: string) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    return deleteData({ endpointData: DDE_ENDPOINTS.Operators.Operator.delete(operatorId) })
  }

  throw new Error('Unsupported platform')
}
